import type {ReactNode} from 'react';
import {ArrowRight, BadgeCheck, CalendarDays, Gamepad2, Languages, MonitorCog, Users} from 'lucide-react';
import {EditorialAuthorLink} from './EditorialAuthor';

type MarvelTokonLaunchNewsPageProps = {
  header: ReactNode;
  footer: ReactNode;
};

const psStore =
  'https://store.playstation.com/pt-br/product/JP9000-PPSA15595_00-MTFS000000000000/';
const psBlog =
  'https://blog.playstation.com/2026/02/12/marvel-tokon-fighting-souls-arrives-on-ps5-pc-august-6/';
const marvelPage = 'https://www.marvel.com/games/marvel-tokon-fighting-souls';
const steamPage = 'https://store.steampowered.com/app/3787240?l=brazilian';
const epicPage =
  'https://store.epicgames.com/pt-BR/p/marvel-tokon-fighting-souls-a3ecbd';

export function MarvelTokonLaunchNewsPage({header, footer}: MarvelTokonLaunchNewsPageProps) {
  return (
    <main className="site-shell article-shell news-article-shell">
      {header}
      <article className="ps5-article news-article marvel-tokon-news">
        <header className="article-hero">
          <div className="article-hero-copy">
            <a className="breadcrumb" href="/noticias">GuiaSite / Notícias / Games</a>
            <p className="section-kicker">Lançamento · Marvel e PlayStation</p>
            <h1>MARVEL Tōkon é lançado hoje: preço, plataformas e edições no Brasil</h1>
            <p className="article-lead">
              O jogo de luta 4x4 da Arc System Works chega em 6 de agosto ao PS5 e PC, com 20
              personagens, dublagem em português brasileiro e três edições. Veja preços,
              diferenças e o que você precisa para jogar online.
            </p>
            <div className="article-meta">
              <EditorialAuthorLink />
              <span><time dateTime="2026-08-06">Publicado em 6 de agosto de 2026</time></span>
              <span>Leitura útil: 8 min</span>
            </div>
          </div>
          <figure className="article-hero-media news-hero-media">
            <img loading="eager"
              src="/guisite/news-marvel-tokon-lancamento-oficial.webp"
              alt="Arte oficial de MARVEL Tōkon Fighting Souls com heróis e vilões do elenco"
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
            />
            <figcaption>Imagem oficial: Sony Interactive Entertainment / PlayStation Blog.</figcaption>
          </figure>
        </header>

        <div className="article-layout">
          <aside className="article-toc" aria-label="Índice da notícia">
            <strong>Nesta notícia</strong>
            <a href="#resumo-marvel-tokon">Resumo direto</a>
            <a href="#preco-marvel-tokon">Preço no Brasil</a>
            <a href="#edicoes-marvel-tokon">Qual edição comprar</a>
            <a href="#plataformas-marvel-tokon">PS5 e PC</a>
            <a href="#conteudo-marvel-tokon">Modos e personagens</a>
            <a href="#online-marvel-tokon">PS Plus e online</a>
            <a href="#pc-marvel-tokon">Requisitos no PC</a>
            <a href="#faq-marvel-tokon">Perguntas frequentes</a>
            <a href="#fontes-marvel-tokon">Fontes</a>
          </aside>

          <div className="article-body">
            <section className="answer-card" id="resumo-marvel-tokon">
              <p className="section-kicker">Resposta direta</p>
              <h2>MARVEL Tōkon: Fighting Souls chega hoje ao PS5 e PC.</h2>
              <p>
                O novo jogo de luta da <strong>Arc System Works</strong>, produzido em parceria com
                PlayStation Studios e Marvel Games, reúne equipes de quatro personagens em
                combates com visual inspirado em anime. No PS5, a edição padrão custa
                <strong> R$ 339,90</strong>.
              </p>
              <p>
                Para a maioria, a edição padrão é a escolha racional: ela traz o jogo completo,
                os 20 personagens de lançamento, modo história, treino, versus local e partidas
                online. Deluxe e Ultimate cobram antecipadamente por personagens, estágio e
                cosméticos que serão distribuídos depois.
              </p>
              <div className="news-signal-row" aria-label="Informações principais">
                <span><CalendarDays size={18} /> Lançamento em 6 de agosto</span>
                <span><Gamepad2 size={18} /> PS5, Steam e Epic</span>
                <span><Languages size={18} /> Áudio em português do Brasil</span>
              </div>
            </section>

            <section id="preco-marvel-tokon">
              <p className="section-kicker">Valores oficiais</p>
              <h2>Quanto custa MARVEL Tōkon no Brasil?</h2>
              <div className="news-fact-table" role="table" aria-label="Preços de MARVEL Tōkon na PlayStation Store Brasil">
                <div role="row"><strong>Edição</strong><strong>Preço no PS5</strong><strong>Conteúdo principal</strong></div>
                <div role="row"><span>Padrão</span><span>R$ 339,90</span><span>Jogo completo</span></div>
                <div role="row"><span>Digital Deluxe</span><span>R$ 484,50</span><span>Jogo + Passe de Ano 1 + itens de lobby</span></div>
                <div role="row"><span>Ultimate</span><span>R$ 569,90</span><span>Deluxe + cinco trajes + coloração animada</span></div>
              </div>
              <p className="source-note">
                Preços consultados na PlayStation Store Brasil em 6 de agosto de 2026. Valores e
                condições podem mudar; confirme o preço atual antes de concluir a compra.
              </p>
              <a className="text-link" href={psStore} target="_blank" rel="noopener noreferrer">
                Ver preço atual na PlayStation Store <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="edicoes-marvel-tokon">
              <h2>Qual edição de MARVEL Tōkon vale mais a pena?</h2>
              <div className="recommendation-grid news-pick-grid">
                <div>
                  <strong>Escolha racional: Padrão</strong>
                  <p>
                    Indicada para quem quer testar o elenco, jogar a campanha e descobrir se
                    continuará no competitivo. A diferença para a Deluxe é alta e não melhora o
                    jogo básico no lançamento.
                  </p>
                </div>
                <div>
                  <strong>Para quem ficará no jogo: Deluxe</strong>
                  <p>
                    Inclui quatro personagens e um estágio do Ano 1, além de Howard, o Pato, e
                    Cosmo como itens de lobby. Faz sentido quando você já pretende acompanhar todo
                    o primeiro ciclo de conteúdo.
                  </p>
                </div>
                <div>
                  <strong>Para colecionadores: Ultimate</strong>
                  <p>
                    Acrescenta cinco trajes, incluindo o Advanced Suit 2.0 do Homem-Aranha, e
                    coloração animada para os 20 personagens. O ganho é predominantemente
                    cosmético.
                  </p>
                </div>
              </div>
              <div className="note-box">
                <BadgeCheck aria-hidden="true" />
                <div>
                  <strong>O Passe de Ano 1 não entrega tudo hoje.</strong>
                  <p>
                    A PlayStation informa que o passe inclui quatro personagens e um estágio
                    distribuídos após o lançamento. Comprar a edição mais cara significa pagar
                    agora por conteúdo futuro.
                  </p>
                </div>
              </div>
            </section>

            <section id="plataformas-marvel-tokon">
              <p className="section-kicker">Disponibilidade</p>
              <h2>MARVEL Tōkon está disponível em quais plataformas?</h2>
              <p>
                O jogo foi lançado para <strong>PlayStation 5</strong> e
                <strong> PC, via Steam e Epic Games Store</strong>. Não existe versão anunciada
                para PS4, Xbox Series X|S, Xbox One ou Nintendo Switch 2 nesta data.
              </p>
              <p>
                No PS5, a página oficial lista aprimoramento para PS5 Pro, áudio 3D, resposta tátil
                do DualSense e imagem com upscale para 4K em tela compatível. No PC, a página do
                Steam informa batalhas travadas em 60 quadros por segundo e suporte a 4K, sujeito
                ao hardware.
              </p>
            </section>

            <section id="conteudo-marvel-tokon">
              <h2>O que vem no jogo no lançamento?</h2>
              <p>
                A seleção inicial reúne <strong>20 heróis e vilões</strong>. O sistema permite
                combinar quatro integrantes, explorar sinergias e trocar o ritmo da luta por meio
                de assistências. Os cenários são inspirados em locais da Marvel e podem ter
                transições durante o combate.
              </p>
              <div className="news-fact-table" role="table" aria-label="Modos de MARVEL Tōkon Fighting Souls">
                <div role="row"><strong>Modo</strong><strong>Formato</strong><strong>Para quem serve</strong></div>
                <div role="row"><span>Episode Mode</span><span>Um jogador</span><span>Histórias em formato de motion comic</span></div>
                <div role="row"><span>Versus local</span><span>1 a 2 jogadores</span><span>Amigos no mesmo sistema</span></div>
                <div role="row"><span>Casual e ranqueado</span><span>Online</span><span>Partidas e progressão competitiva</span></div>
                <div role="row"><span>Treinamento</span><span>Offline</span><span>Combos, equipes e situações configuráveis</span></div>
                <div role="row"><span>Lobby online</span><span>Até 64 jogadores</span><span>Encontrar partidas e socializar</span></div>
              </div>
              <p>
                A história tem dublagem em dez idiomas, incluindo
                <strong> português brasileiro</strong>. A PlayStation Store também lista legendas
                grandes, leitor de tela básico, remapeamento, dificuldade ajustável e outras
                opções de acessibilidade.
              </p>
            </section>

            <section id="online-marvel-tokon">
              <h2>Precisa de PS Plus para jogar MARVEL Tōkon?</h2>
              <p>
                <strong>Para jogar online no PS5, sim.</strong> A PlayStation Store informa que
                uma assinatura ativa do PlayStation Plus é necessária para partidas online. Os
                modos offline, como Episode Mode, treino e versus local, não dependem da
                assinatura.
              </p>
              <p>
                No PC, não existe cobrança equivalente ao PS Plus, mas o jogo exige conta e
                conexão conforme a loja utilizada e os serviços online. A Epic Games Store lista
                suporte a multijogador multiplataforma, enquanto a página do Steam destaca
                rollback netcode.
              </p>
              <a className="text-link" href="/comparar/ps-plus-vs-game-pass/">
                Entender os planos do PS Plus <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="pc-marvel-tokon">
              <p className="section-kicker">Versão para computador</p>
              <h2>Requisitos de MARVEL Tōkon no PC</h2>
              <div className="news-fact-table" role="table" aria-label="Requisitos de MARVEL Tōkon no PC">
                <div role="row"><strong>Componente</strong><strong>Mínimo</strong><strong>Recomendado</strong></div>
                <div role="row"><span>Sistema</span><span>Windows 11</span><span>Windows 11</span></div>
                <div role="row"><span>Processador</span><span>Core i5-8400 ou Ryzen 5 1600X</span><span>Core i7-8700 ou Ryzen 5 3600X</span></div>
                <div role="row"><span>Memória</span><span>16 GB</span><span>16 GB</span></div>
                <div role="row"><span>Placa de vídeo</span><span>RTX 2060 6 GB ou RX 6600 XT 8 GB</span><span>RTX 3070 8 GB ou RX 7600 XT 16 GB</span></div>
              </div>
              <div className="note-box">
                <MonitorCog aria-hidden="true" />
                <div>
                  <strong>Confira a loja novamente antes de comprar.</strong>
                  <p>
                    Requisitos podem ser ajustados após o lançamento. Use os valores acima como
                    referência da página oficial consultada hoje, não como promessa de desempenho
                    idêntico em toda configuração.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2>MARVEL Tōkon é acessível para quem não domina jogos de luta?</h2>
              <p>
                O projeto inclui controles tradicionais e uma opção rápida, sequências de combo
                simplificadas, tutorial interativo e treinamento configurável. Isso reduz a
                barreira inicial, mas não elimina a profundidade de montar equipes, administrar
                assistências e aprender confrontos.
              </p>
              <p>
                Para quem chega por causa da Marvel, o Episode Mode e o versus local oferecem um
                caminho menos competitivo. Para quem vem de Dragon Ball FighterZ ou Guilty Gear,
                o principal atrativo está nas equipes 4x4 e na linguagem visual da Arc System
                Works.
              </p>
            </section>

            <section id="faq-marvel-tokon">
              <h2>Perguntas frequentes sobre MARVEL Tōkon</h2>
              <div className="faq-list">
                <details>
                  <summary>Quanto custa MARVEL Tōkon no Brasil?</summary>
                  <p>Em 6 de agosto de 2026, a edição padrão custa R$ 339,90 na PlayStation Store Brasil. A Deluxe custa R$ 484,50 e a Ultimate, R$ 569,90.</p>
                </details>
                <details>
                  <summary>MARVEL Tōkon está disponível para Xbox ou Switch 2?</summary>
                  <p>Não. As plataformas anunciadas no lançamento são PS5 e PC, via Steam e Epic Games Store.</p>
                </details>
                <details>
                  <summary>MARVEL Tōkon tem português brasileiro?</summary>
                  <p>Sim. O jogo oferece texto e dublagem em português brasileiro.</p>
                </details>
                <details>
                  <summary>Precisa de PS Plus para jogar?</summary>
                  <p>O PS Plus é exigido para partidas online no PS5. Modos offline e versus local não dependem da assinatura.</p>
                </details>
                <details>
                  <summary>Quantos personagens existem no lançamento?</summary>
                  <p>O elenco inicial tem 20 personagens. O Passe de Ano 1 prevê mais quatro personagens e um estágio após o lançamento.</p>
                </details>
              </div>
            </section>

            <section className="final-verdict">
              <p className="section-kicker">Conclusão GuiaSite</p>
              <h2>A edição padrão é a compra mais segura no lançamento.</h2>
              <p>
                MARVEL Tōkon chega com uma proposta clara, localização brasileira completa e
                conteúdo para quem joga sozinho, localmente ou online. A Deluxe só se justifica
                para quem já decidiu acompanhar o Ano 1; a Ultimate cobra uma diferença grande
                por cosméticos. Para todos os demais, comece pelo jogo base.
              </p>
            </section>

            <section id="fontes-marvel-tokon">
              <p className="section-kicker">Transparência editorial</p>
              <h2>Fontes e atualização</h2>
              <p>
                Notícia publicada em 6 de agosto de 2026 com dados de fontes primárias. Preços,
                recursos de PS5, classificação e condições do PS Plus foram consultados na
                PlayStation Store Brasil. Plataformas, edições, modos e localização foram
                conferidos nas páginas oficiais da PlayStation, Marvel, Steam e Epic Games.
              </p>
              <ul className="source-list">
                <li><a href={psStore} target="_blank" rel="noopener noreferrer">PlayStation Store Brasil: preço, edições e recursos</a></li>
                <li><a href={psBlog} target="_blank" rel="noopener noreferrer">PlayStation Blog: lançamento, modos e conteúdo das edições</a></li>
                <li><a href={marvelPage} target="_blank" rel="noopener noreferrer">Marvel: página oficial de MARVEL Tōkon</a></li>
                <li><a href={steamPage} target="_blank" rel="noopener noreferrer">Steam: versão para PC e recursos técnicos</a></li>
                <li><a href={epicPage} target="_blank" rel="noopener noreferrer">Epic Games Store: requisitos e idiomas</a></li>
              </ul>
            </section>

            <section className="related-reading">
              <p className="section-kicker">Continue decidindo melhor</p>
              <h2>Leituras relacionadas</h2>
              <div className="recommendation-grid">
                <a href="/games/playstation-5/">
                  <strong>PS5 ainda vale a pena em 2026?</strong>
                  <p>Biblioteca, vida útil e o impacto de esperar pelo PS6.</p>
                </a>
                <a href="/comparar/ps-plus-vs-game-pass/">
                  <strong>PS Plus ou Game Pass?</strong>
                  <p>Planos, catálogos e valor real para cada perfil.</p>
                </a>
                <a href="/noticias/ps-plus-agosto-2026-jogos-mensais">
                  <strong>Jogos do PS Plus de agosto</strong>
                  <p>Veja o que resgatar e qual título jogar primeiro.</p>
                </a>
              </div>
            </section>
          </div>
        </div>
      </article>
      {footer}
    </main>
  );
}


